import type {
  AccountInfo,
  RedirectRequest,
  SilentRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';
import { defineStore } from 'pinia';

import { MSAL_CONFIG } from '@/constants';
import { accountWithMsalIdToken } from '@/helper/auth/oauth-account';
import { validateToken } from '@/helper/auth/validation';
import matomoHelper from '@/helper/matomo';
import {
  type AuthStrategy,
  exchangeCodeForTokens as exchangeCodeForTokensHelper,
  getAuthStrategy,
  refreshTokensWithRefreshToken,
} from './auth/strategies';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  isRedirecting: boolean;
}

// Helper to check if running on native platform
const isNative = () => Capacitor.isNativePlatform();

// Get the appropriate redirect URI based on platform (HTTPS for web/PWA, msal:// for native)
const getRedirectUri = (): string => {
  if (isNative()) {
    const nativeUri = import.meta.env.VITE_MSAL_REDIRECT_URI_NATIVE;
    if (nativeUri) {
      return nativeUri;
    }
  }
  return import.meta.env.VITE_MSAL_REDIRECT_URI;
};

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
    authority:
      'https://login.microsoftonline.com/' +
      import.meta.env.VITE_MSAL_TENANT_ID,
    redirectUri: getRedirectUri(),
    // Avoid MSAL navigating back to a cached start URL after custom-scheme redirects (Capacitor).
    ...(isNative() ? { navigateToLoginRequestUrl: false as const } : {}),
  },
  cache: {
    // Keep MSAL cache persistent across full WebView reloads/restarts.
    cacheLocation: 'localStorage' as const,
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const apiTokenScopes = () => MSAL_CONFIG.SCOPES.FULL.split(' ');

/** Short log in all builds so device consoles show that getAllAccounts() ran. */
function logMsalAccountCheckSummary(stage: string, accountCount: number): void {
  try {
    const hasBackupUser = Boolean(
      localStorage.getItem(MSAL_CONFIG.STORAGE_KEYS.USER),
    );
    console.log(
      `[msal:${stage}] getAllAccounts=${accountCount} backupUserInLS=${hasBackupUser}`,
    );
  } catch {
    console.log(`[msal:${stage}] getAllAccounts=${accountCount}`);
  }
}

/** Skip redundant setUser when acquireTokenSilent returned the same JWTs (axios calls this often). */
function userSessionTokensUnchanged(
  user: User | null,
  idToken: string,
  accessToken: string,
  localAccountId: string,
): boolean {
  if (!user) {
    return false;
  }
  if (user.id !== localAccountId || user.token !== idToken) {
    return false;
  }
  return (accessToken || '') === (user.access_token || '');
}

function logMsalCacheDiagnostics(stage: string): void {
  try {
    const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
    const allKeys = Object.keys(localStorage);
    const msalKeys = allKeys.filter(
      (k) =>
        k.startsWith('msal.') ||
        k.includes('-login.microsoftonline.com-') ||
        k.includes('-login.windows.net-'),
    );
    const scopedKeys = msalKeys.filter(
      (k) =>
        k.includes(clientId) ||
        k.startsWith(`msal.${clientId}.`) ||
        k.includes('active-account'),
    );
    const accountEntityKeys = scopedKeys.filter((k) => k.endsWith('-account'));
    const tokenEntityKeys = scopedKeys.filter(
      (k) =>
        k.includes('-accesstoken-') ||
        k.includes('-refreshtoken-') ||
        k.includes('-idtoken-') ||
        k.includes('|accesstoken|') ||
        k.includes('|refreshtoken|') ||
        k.includes('|idtoken|'),
    );
    const activeAccountFilterKey = scopedKeys.find((k) =>
      k.includes('active-account'),
    );
    const tokenKeysKey = scopedKeys.find(
      (k) => k.includes('token.keys') || k.includes('.token.keys.'),
    );

    console.log(`[msal-cache:${stage}] total msal keys`, msalKeys.length);
    console.log(
      `[msal-cache:${stage}] scoped keys`,
      scopedKeys.length,
      scopedKeys,
    );
    console.log(
      `[msal-cache:${stage}] account entities`,
      accountEntityKeys.length,
      accountEntityKeys,
    );
    console.log(
      `[msal-cache:${stage}] token entities`,
      tokenEntityKeys.length,
      tokenEntityKeys,
    );
    if (activeAccountFilterKey) {
      console.log(
        `[msal-cache:${stage}] active-account raw`,
        localStorage.getItem(activeAccountFilterKey),
      );
    }
    if (tokenKeysKey) {
      console.log(
        `[msal-cache:${stage}] token.keys raw`,
        localStorage.getItem(tokenKeysKey),
      );
    }
  } catch (error) {
    console.warn(`[msal-cache:${stage}] diagnostics failed`, error);
  }
}

function isInteractionRequiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const e = error as { name?: string; errorCode?: string };
  if (e.name === 'InteractionRequiredAuthError') {
    return true;
  }
  switch (e.errorCode) {
    case 'interaction_required':
    case 'consent_required':
    case 'login_required':
      return true;
    default:
      return false;
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    loading: false,
    error: null,
    initialized: false,
    isRedirecting: false,
  }),
  actions: {
    getBestEffortLoginHint(): string | null {
      if (this.user?.username) {
        return this.user.username;
      }
      try {
        const storedUserStr = localStorage.getItem(MSAL_CONFIG.STORAGE_KEYS.USER);
        if (!storedUserStr) {
          return null;
        }
        const storedUser = JSON.parse(storedUserStr) as {
          username?: string;
          email?: string;
        };
        return storedUser.username || storedUser.email || null;
      } catch {
        return null;
      }
    },

    async trySsoSilentFallback(): Promise<boolean> {
      const loginHint = this.getBestEffortLoginHint();
      if (!loginHint) {
        return false;
      }
      try {
        const request: SsoSilentRequest = {
          scopes: apiTokenScopes(),
          loginHint,
          redirectUri: getRedirectUri(),
        };
        const result = await msalInstance.ssoSilent(request);
        if (!result.account || !result.idToken) {
          return false;
        }
        await msalInstance.setActiveAccount(result.account);
        this.setUser(
          accountWithMsalIdToken(result),
          result.accessToken,
          result.expiresOn || undefined,
        );
        logMsalCacheDiagnostics('after-ssoSilent');
        return true;
      } catch (error) {
        if (!isInteractionRequiredError(error)) {
          console.warn('checkAccount - ssoSilent fallback failed:', error);
        }
        return false;
      }
    },

    async initializeMsal(): Promise<boolean> {
      if (this.initialized) {
        return true;
      }
      try {
        await msalInstance.initialize();
        // Clear any stuck interaction states that might cause issues
        // This is safer than handleRedirectPromise() which can trigger popups
        this.clearStuckInteractionState();
        this.initialized = true;
        return true;
      } catch (error) {
        console.error('MSAL Initialization Error:', error);
        return false;
      }
    },

    /**
     * Clear stuck MSAL interaction state from session storage
     * This helps resolve "interaction in progress" errors
     * 
     * IMPORTANT: This only removes interaction state flags, NOT account data or tokens.
     * Active sessions remain intact. MSAL stores:
     * - Accounts: msal.{clientId}.accounts (NOT touched)
     * - Tokens: msal.{clientId}.accesstoken.* (NOT touched)
     * - Interaction state: msal.{clientId}.interaction.* (ONLY these are removed)
     */
    clearStuckInteractionState(): void {
      try {
        const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
        
        // MSAL stores interaction state with keys like:
        // - msal.{clientId}.interaction.status
        // - msal.{clientId}.interaction.{requestId}
        // We ONLY remove keys containing ".interaction." to preserve accounts and tokens
        
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            key.startsWith('msal.') &&
            key.includes('.interaction.')
          ) {
            // Double-check: ensure we're not touching account or token keys
            // Account keys: msal.{clientId}.accounts
            // Token keys: msal.{clientId}.accesstoken.* or msal.{clientId}.idtoken.*
            if (
              !key.includes('.accounts') &&
              !key.includes('.accesstoken') &&
              !key.includes('.idtoken') &&
              !key.includes('.refreshtoken')
            ) {
              keysToRemove.push(key);
            }
          }
        }
        
        keysToRemove.forEach((key) => {
          sessionStorage.removeItem(key);
          console.log('Removed stuck interaction state key:', key);
        });
        
        if (keysToRemove.length > 0) {
          console.log(
            `Cleared ${keysToRemove.length} stuck MSAL interaction state key(s)`,
          );
        }
      } catch (error) {
        console.warn('Error clearing interaction state:', error);
      }
    },

    /**
     * Get the current authentication strategy (Web or Native)
     */
    getAuthStrategy(): AuthStrategy {
      return getAuthStrategy(
        (account) => this.setUser(account),
        () => this.initLocaleStorage(),
        () => this.redirectAfterLogin(),
        exchangeCodeForTokensHelper,
      );
    },

    async login() {
      // If login is already in progress, show error message instead of silently ignoring
      if (this.loading) {
        console.warn('Login already in progress, showing error message');
        this.error =
          'Fehler im Anmeldeprozess. Bitte lade die Seite neu und versuche es erneut.';
        // Try to clear the stuck interaction state
        this.clearStuckInteractionState();
        return;
      }

      this.loading = true;
      this.error = null;

      const initialized = await this.initializeMsal();
      if (!initialized) {
        this.loading = false;
        return;
      }

      try {
        const strategy = this.getAuthStrategy();
        await strategy.login(msalInstance, getRedirectUri, msalConfig);
      } catch (err) {
        console.error('Login error:', err);
        
        // Handle MSAL "interaction in progress" error specifically
        const errorMessage = err instanceof Error ? err.message : String(err);
        const errorCode = (err as any)?.errorCode;
        
        if (
          errorCode === 'interaction_in_progress' ||
          errorMessage.toLowerCase().includes('interaction in progress') ||
          errorMessage.toLowerCase().includes('interaction is currently in progress')
        ) {
          // Try to clear the stuck interaction state
          this.clearStuckInteractionState();
          
          // Provide user-friendly error with reload suggestion
          this.error =
            'Fehler im Anmeldeprozess. Bitte lade die Seite neu und versuche es erneut.';
        } else {
          this.error = errorMessage || 'Ein Fehler ist aufgetreten';
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Handle authentication redirect from native OAuth flow
     */
    async handleAuthRedirect(url: string): Promise<void> {
      // Keep loading true until we finish so AuthLogin's watch does not start a
      // parallel redirect/Matomo flow (native login() ends as soon as Browser opens).
      this.loading = true;
      try {
        // Parse the URL to extract the authorization code
        const urlObj = new URL(url);
        const code = urlObj.searchParams.get('code');
        const state = urlObj.searchParams.get('state');
        const error = urlObj.searchParams.get('error');
        const errorDescription = urlObj.searchParams.get('error_description');

        if (error) {
          throw new Error(errorDescription || error);
        }

        // Same native redirect URI is used for logout (postLogoutRedirectUri); Entra
        // returns the custom scheme without OAuth params — not a login callback.
        if (!code) {
          if (state) {
            throw new Error('No authorization code received');
          }
          const { Browser } = await import('@capacitor/browser');
          await Browser.close().catch(() => {});
          return;
        }

        // Validate state (login flow only)
        const storedState = sessionStorage.getItem(
          MSAL_CONFIG.STORAGE_KEYS.LOGIN_STATE,
        );
        if (!state || state !== storedState) {
          throw new Error('Invalid state parameter');
        }

        // Get code verifier for PKCE
        const codeVerifier = sessionStorage.getItem(
          MSAL_CONFIG.STORAGE_KEYS.LOGIN_CODE_VERIFIER,
        );
        if (!codeVerifier) {
          throw new Error('Code verifier not found');
        }

        // Clean up session storage
        sessionStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.LOGIN_STATE);
        sessionStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.LOGIN_NONCE);
        sessionStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.LOGIN_CODE_VERIFIER);

        // Close the browser
        const { Browser } = await import('@capacitor/browser');
        await Browser.close();

        // Exchange the authorization code for tokens
        const tokenResponse = await exchangeCodeForTokensHelper(
          code,
          codeVerifier,
          msalInstance,
          getRedirectUri,
          msalConfig,
        );
        logMsalCacheDiagnostics('after-token-exchange');
        console.log('Token response:', JSON.stringify(tokenResponse));

        if (!tokenResponse || !tokenResponse.account) {
          throw new Error('Failed to process authentication response');
        }
        console.log('Login response:' + JSON.stringify(tokenResponse));
        this.setUser(
          accountWithMsalIdToken(tokenResponse),
          tokenResponse.accessToken,
          tokenResponse.expiresOn || undefined,
          tokenResponse.refreshToken,
        );
        await this.initLocaleStorage();
        this.redirectAfterLogin();
      } catch (err) {
        console.error('Error handling auth redirect:', err);
        this.error =
          err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten';
        const { Browser } = await import('@capacitor/browser');
        await Browser.close().catch(() => {});
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      this.loading = true;
      this.isRedirecting = false;
      this.clearUser();
      this.clearReturnUrl();

      if (isNative()) {
        // Router guards only run on navigation; without this, /profile stays mounted
        // while MSAL opens microsoftonline (in-app browser or WebView navigation).
        const { default: appRouter } = await import('@/router');
        await appRouter.replace({ name: 'home' });

        // Web reloads the page on logout, but the native WebView keeps running:
        // re-fetch cached content anonymously so internal-only data does not
        // survive the logout. Dynamic imports avoid a circular dependency.
        const [{ useStoriesStore }, { useHomepageStore }] = await Promise.all([
          import('./stories'),
          import('./homepage'),
        ]);
        useStoriesStore().fetchStories();
        useHomepageStore().fetchHomepage();
      }

      try {
        const strategy = this.getAuthStrategy();
        await strategy.logout(msalInstance, getRedirectUri);
      } catch (err) {
        console.error('Logout error:', err);
      } finally {
        this.loading = false;
        if (!isNative()) {
          location.href = '/';
        }
      }
    },

    /**
     * Ensure Pinia + localStorage USER match a token MSAL can still renew (silent refresh).
     * Does not use AccountInfo alone — stale idToken / expired JWT must not show as logged in.
     */
    async hydrateUserFromMsalAccount(account: AccountInfo): Promise<boolean> {
      try {
        const silentRequest: SilentRequest = {
          account,
          scopes: apiTokenScopes(),
        };
        const silentResult =
          await msalInstance.acquireTokenSilent(silentRequest);
        const acc = accountWithMsalIdToken(silentResult);
        if (!acc.idToken) {
          console.warn('hydrateUserFromMsalAccount: no id token from silent');
          this.clearUser();
          return false;
        }
        if (!silentResult.accessToken) {
          console.warn('hydrateUserFromMsalAccount: no access token from silent');
          this.clearUser();
          return false;
        }
        this.setUser(
          acc,
          silentResult.accessToken,
          silentResult.expiresOn || undefined,
        );
        return true;
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          console.log(
            'hydrateUserFromMsalAccount: re-login required, clearing session',
          );
          this.clearUser();
          return false;
        }
        console.warn(
          'hydrateUserFromMsalAccount: silent refresh failed, trying backup',
          error,
        );
        await this.restoreUserFromStorage();
        if (!this.user) {
          this.clearUser();
        }
        return this.user !== null;
      }
    },

    /**
     * Check if there's an active account and restore user if valid
     */
    async checkAccount(): Promise<boolean> {
      console.log('[auth] checkAccount: begin');
      const initialized = await this.initializeMsal();
      console.log('[auth] checkAccount: initializeMsal ->', initialized);
      if (!initialized) {
        return false;
      }

      const accounts = msalInstance.getAllAccounts();
      logMsalAccountCheckSummary('checkAccount', accounts.length);
      logMsalCacheDiagnostics('checkAccount');
      console.log('checkAccount - found accounts:', accounts.length);
      if (accounts.length) {
        return await this.hydrateUserFromMsalAccount(accounts[0]);
      }

      console.log('checkAccount - no accounts found in MSAL cache');

      // ssoSilent uses a hidden iframe; on Capacitor/WKWebView it often hits monitor_window_timeout
      // and blocks startup for seconds. Native recovery uses localStorage + refresh_token instead.
      if (!isNative()) {
        const recoveredWithSsoSilent = await this.trySsoSilentFallback();
        if (recoveredWithSsoSilent) {
          console.log('checkAccount - recovered account via ssoSilent fallback');
          return true;
        }
      } else {
        console.log(
          'checkAccount - skipping ssoSilent on native (use localStorage / refresh_token)',
        );
      }

      // Fallback: Try to restore user from localStorage (native may refresh via refresh_token)
      return await this.restoreUserFromStorage();
    },

    /**
     * Restore user from localStorage if access token is valid, or refresh on native using refresh_token.
     */
    async restoreUserFromStorage(): Promise<boolean> {
      try {
        const storedUserStr = localStorage.getItem(
          MSAL_CONFIG.STORAGE_KEYS.USER,
        );
        if (!storedUserStr) {
          console.log('checkAccount - no user found in localStorage either');
          this.user = null;
          return false;
        }

        const storedUser = JSON.parse(storedUserStr) as User;
        console.log(
          'checkAccount - found user in localStorage, validating token...',
        );

        const isTokenValid = validateToken(
          storedUser.token,
          storedUser.token_expires,
        );

        if (isTokenValid) {
          this.user = storedUser;
          console.log(
            'checkAccount - restored user from localStorage (token is valid)',
          );
          return true;
        }

        if (
          isNative() &&
          typeof storedUser.refresh_token === 'string' &&
          storedUser.refresh_token.length > 0
        ) {
          console.log(
            'checkAccount - access token expired; attempting native refresh',
          );
          try {
            const result = await refreshTokensWithRefreshToken(
              storedUser.refresh_token,
              msalInstance,
              msalConfig,
              { fallbackIdToken: storedUser.oidc_id_token },
            );
            const nextRefresh =
              result.refreshToken ?? storedUser.refresh_token;
            this.setUser(
              accountWithMsalIdToken(result),
              result.accessToken,
              result.expiresOn || undefined,
              nextRefresh,
            );
            console.log(
              'checkAccount - session extended via refresh_token',
            );
            return true;
          } catch (refreshError) {
            console.warn(
              'checkAccount - native refresh failed, clearing session',
              refreshError,
            );
          }
        }

        console.log(
          'checkAccount - stored user token is expired or invalid, clearing',
        );
        localStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.USER);
        this.user = null;
      } catch (error) {
        console.error(
          'checkAccount - error restoring from localStorage:',
          error,
        );
        localStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.USER);
        this.user = null;
      }
      return false;
    },

    setUser(
      account: AccountInfo,
      accessToken?: string,
      expiresOn?: Date,
      refreshToken?: string,
    ): void {
      console.log('Setting user:', JSON.stringify(account));
      console.log('Access Token:', accessToken);
      console.log('ID Token:', account.idToken);

      // API uses OIDC id_token; exp from id_token claims (not Graph access token lifetime).
      let tokenExpires = '';
      if (account.idTokenClaims?.['exp']) {
        tokenExpires = new Date(account.idTokenClaims['exp'] * 1000).toString();
      } else if (expiresOn) {
        tokenExpires = expiresOn.toString();
      }

      const mergedRefresh =
        refreshToken !== undefined ? refreshToken : this.user?.refresh_token;
      const mergedOidc = account.idToken || this.user?.oidc_id_token;
      const mergedAccess =
        accessToken !== undefined && accessToken !== ''
          ? accessToken
          : this.user?.access_token;

      this.user = {
        name: account.name?.split(' ')[0] || '',
        initials:
          account.name
            ?.split(' |')[0]
            .split(/[\s]+/)
            .map((n) => n.charAt(0).toUpperCase())
            .join('') || '',
        username: account.username,
        email: account.username,
        token: account.idToken || '',
        token_expires: tokenExpires,
        id: account.localAccountId,
        ...(mergedAccess ? { access_token: mergedAccess } : {}),
        ...(mergedRefresh ? { refresh_token: mergedRefresh } : {}),
        ...(mergedOidc ? { oidc_id_token: mergedOidc } : {}),
      };
      if (import.meta.env.DEV) {
        console.log('User set:', JSON.stringify(this.user));
      }

      // Store user in localStorage as backup
      // This ensures the user persists even if MSAL cache is cleared
      try {
        localStorage.setItem(
          MSAL_CONFIG.STORAGE_KEYS.USER,
          JSON.stringify(this.user),
        );
      } catch (error) {
        console.warn('Failed to store user in localStorage:', error);
      }
    },

    clearUser(): void {
      this.user = null;
      localStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.USER);
    },

    /**
     * Fresh OIDC id_token for backend (X-Authorization). MSAL may renew access/id tokens silently.
     */
    async acquireToken(scopes: string[]): Promise<string> {
      const account = msalInstance.getAllAccounts()[0];

      // No MSAL account: use valid cached JWT or native refresh_token
      if (!account) {
        if (
          this.user?.token &&
          validateToken(this.user.token, this.user.token_expires)
        ) {
          return this.user.token;
        }
        if (
          isNative() &&
          this.user?.refresh_token &&
          this.user.refresh_token.length > 0
        ) {
          try {
            const result = await refreshTokensWithRefreshToken(
              this.user.refresh_token,
              msalInstance,
              msalConfig,
              { fallbackIdToken: this.user.oidc_id_token },
            );
            const nextRefresh =
              result.refreshToken ?? this.user.refresh_token;
            this.setUser(
              accountWithMsalIdToken(result),
              result.accessToken,
              result.expiresOn || undefined,
              nextRefresh,
            );
            const idTok = result.idToken || result.account?.idToken || '';
            if (!idTok) {
              throw new Error('No id token after native refresh');
            }
            return idTok;
          } catch (refreshError) {
            console.warn('acquireToken: native refresh failed', refreshError);
            this.clearUser();
          }
        }
        throw new Error(
          'No account and no valid token. Please sign in again.',
        );
      }

      try {
        const silentRequest: SilentRequest = {
          scopes,
          account,
        };
        const silentResult =
          await msalInstance.acquireTokenSilent(silentRequest);

        const accessToken = silentResult.accessToken;
        if (!accessToken) {
          throw new Error('No access token received from MSAL');
        }

        const idToken =
          silentResult.idToken || silentResult.account?.idToken || '';
        if (!idToken) {
          throw new Error('No id token received from MSAL');
        }

        if (silentResult.account) {
          const acc = accountWithMsalIdToken(silentResult);
          if (
            !userSessionTokensUnchanged(
              this.user,
              idToken,
              accessToken,
              acc.localAccountId,
            )
          ) {
            this.setUser(
              acc,
              accessToken,
              silentResult.expiresOn || undefined,
            );
          }
        }

        return idToken;
      } catch (error: any) {
        if (error.name !== 'InteractionRequiredAuthError') {
          console.error('Token acquisition error:', error);
          throw error;
        }

        if (isNative()) {
          // For native, use redirect flow
          const redirectRequest: RedirectRequest = {
            scopes,
            redirectUri: getRedirectUri(),
          };
          await msalInstance.acquireTokenRedirect(redirectRequest);
          // The redirect will be handled by the app listener
          throw new Error('Token acquisition requires user interaction');
        }

        try {
          const interactiveResult = await msalInstance.acquireTokenPopup({
            scopes,
          });

          const accessToken = interactiveResult.accessToken;
          if (!accessToken) {
            throw new Error('No access token received from MSAL');
          }

          const idToken =
            interactiveResult.idToken ||
            interactiveResult.account?.idToken ||
            '';
          if (!idToken) {
            throw new Error('No id token received from MSAL');
          }

          if (interactiveResult.account) {
            const acc = accountWithMsalIdToken(interactiveResult);
            if (
              !userSessionTokensUnchanged(
                this.user,
                idToken,
                accessToken,
                acc.localAccountId,
              )
            ) {
              this.setUser(
                acc,
                accessToken,
                interactiveResult.expiresOn || undefined,
              );
            }
          }

          return idToken;
        } catch (popupError: any) {
          // Handle MSAL "interaction in progress" error
          const errorMessage = popupError?.message || String(popupError);
          const errorCode = popupError?.errorCode;
          
          if (
            errorCode === 'interaction_in_progress' ||
            errorMessage.toLowerCase().includes('interaction in progress') ||
            errorMessage.toLowerCase().includes('interaction is currently in progress')
          ) {
            throw new Error(
              'Fehler im Anmeldeprozess. Bitte warten Sie, bis der aktuelle Vorgang abgeschlossen ist.',
            );
          }
          throw popupError;
        }
      }
    },

    /**
     * Initialize locale storage and track first login
     */
    async initLocaleStorage(): Promise<void> {
      localStorage.removeItem('fcmSubscribed');

      if (!localStorage.getItem('firstLoginEventTriggered')) {
        await matomoHelper.trackEvent({
          e_c: 'Auth',
          e_a: 'FirstLogin',
          e_n: '1',
          e_v: 1,
        });
        localStorage.setItem('firstLoginEventTriggered', 'true');
      }
      return;
    },

    setReturnUrl(returnUrl: string | null) {
      if (!returnUrl) {
        localStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.RETURN_URL);
        return;
      }
      localStorage.setItem(MSAL_CONFIG.STORAGE_KEYS.RETURN_URL, returnUrl);
    },

    clearReturnUrl() {
      localStorage.removeItem(MSAL_CONFIG.STORAGE_KEYS.RETURN_URL);
    },

    getReturnUrl(): string | null {
      return localStorage.getItem(MSAL_CONFIG.STORAGE_KEYS.RETURN_URL);
    },

    /**
     * Redirect after successful login
     */
    async redirectAfterLogin() {
      // Prevent multiple redirects
      if (this.isRedirecting) {
        return;
      }
      this.isRedirecting = true;

      // Check if we have a stored return URL in localStorage
      let redirectPath = '/start';
      const storedReturnUrl = this.getReturnUrl();

      if (storedReturnUrl && storedReturnUrl !== '/auth') {
        // Use the stored return URL, but avoid redirecting back to auth page
        redirectPath = storedReturnUrl;
      }

      // Add logged-in=true query parameter if not already present
      const url = new URL(redirectPath, window.location.origin);
      if (!url.searchParams.has('logged-in')) {
        url.searchParams.set('logged-in', 'true');
      }

      await matomoHelper.trackEvent({
        e_c: 'Auth',
        e_a: 'Login',
        e_n: url.pathname,
        e_v: 1,
      });

      this.clearReturnUrl();
      location.href = url.pathname + url.search;
    },
  },
});
